import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** Space kept under a table, down to the bottom of the window (the page's bottom padding). */
const BOTTOM_GAP = 32;
const MIN_HEIGHT = 240;

/**
 * A DataTable's `height` is the maximum height of its scroll area (DSM, 520 px by
 * default): below it the table shrinks to its rows. This hook gives the table all
 * the room left down to the bottom of the window, so it only scrolls inside
 * itself when its rows do not fit on screen. Put the ref on a wrapper of the table.
 */
export function useFillHeight(): [(node: HTMLDivElement | null) => void, number | undefined] {
  const node = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number>();

  const measure = useCallback(() => {
    const wrapper = node.current;
    const scroll = wrapper?.querySelector<HTMLElement>('.kui-dt-scroll');
    if (!wrapper || !scroll) return;
    const s = scroll.getBoundingClientRect();
    // What the table draws under its rows (pagination, totals) stays visible too.
    const below = wrapper.getBoundingClientRect().bottom - s.bottom;
    const next = Math.max(MIN_HEIGHT, Math.floor(window.innerHeight - (s.top + window.scrollY) - below - BOTTOM_GAP));
    setHeight(h => (h === next ? h : next));
  }, []);

  useLayoutEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [measure]);

  const ref = useCallback((n: HTMLDivElement | null) => {
    node.current = n;
    if (n) requestAnimationFrame(measure);
  }, [measure]);

  return [ref, height];
}
